import { useTranslation } from 'react-i18next';
import { mdiCheck, mdiClose } from '@mdi/js';
import { Box } from '@mui/material';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';

import { ConfirmDialog } from 'common/components/ConfirmDialog';
import {
    IDateAttendanceInput,
    IAttendanceStatusChoices,
    IOfferEventDate,
    IOfferEventEnrollmentManageableFragment,
    UpdateAttendanceDocument,
    CurrentUserDocument,
    IRole,
    IOfferEventEnrollmentManagementListItemFragment,
} from 'graphql/types';
import { formatDate } from 'common/utils/formatDate';
import { ButtonToggle } from 'common/components/ButtonToggle';
import { Divider } from 'common/components/Divider';
import { useApolloError } from 'common/hooks/useApolloError';
import { getEnrollmentAttendanceValues } from 'offer/utils/offerEvent';
import { useSnackbar } from 'common/hooks/useSnackbar';

export interface IEnrollmentAttendanceDialogValues {
    [key: string]: IDateAttendanceInput;
}

interface IProps {
    offerEventId: string;
    offerEventTrainers?: string[];
    enrollments: (
        | IOfferEventEnrollmentManageableFragment
        | IOfferEventEnrollmentManagementListItemFragment
    )[];
    dates: IOfferEventDate[];
    open: boolean;
    onClose: () => void;
    onSubmit?: () => void;
}

export const EnrollmentAttendanceDialog = ({
    offerEventId,
    offerEventTrainers,
    enrollments,
    dates,
    open,
    onClose,
    onSubmit,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [updateAttendance, { loading }] = useMutation(
        UpdateAttendanceDocument
    );
    // Use state to avoid text changing when dialog is closed
    const [enrollmentCount, setEnrollmentCount] = useState<number>(
        enrollments.length
    );
    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const currentUser = currentUserData?.currentUser;

    const isManager = currentUser?.roles?.includes(IRole.Manager);
    const isOfferEventTrainer = offerEventTrainers?.includes(
        currentUser?.id || ''
    );

    const filteredDates = dates
        .filter((date) => {
            // If manager or overall offer event trainer, show all dates
            if (isManager || isOfferEventTrainer) return date;

            // If current user is trainer of this date, then the date can be managed
            if (
                date.trainers?.some(
                    (trainer) => trainer?.id === currentUser?.id
                )
            )
                return date;

            return null;
        })
        .filter(Boolean);

    useEffect(() => {
        if (!open) return;

        setEnrollmentCount(enrollments.length);
    }, [enrollments, open]);

    const handleUpdateAttendance = async (
        values: IEnrollmentAttendanceDialogValues
    ) => {
        const dateAttendances = (Object.keys(values) as string[]).map((key) => {
            const currentDateAttendance = values[key];

            return {
                offerEventDateId: key,
                presentUserIds: currentDateAttendance.presentUserIds,
                absentUserIds: currentDateAttendance.absentUserIds,
                noStatusUserIds: currentDateAttendance.noStatusUserIds,
            };
        });

        try {
            await updateAttendance({
                variables: {
                    dateAttendances,
                    offerEventId,
                },
            });

            client.cache.evict({
                fieldName: 'manageableOfferEventEnrollments',
            });
            client.cache.evict({
                fieldName: 'manageableOfferEventEnrollmentsForEvent',
            });
            client.cache.gc();
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('offerEventEnrollmentAttendanceDialog.success'),
            {
                variant: 'success',
            }
        );

        onSubmit?.();

        return;
    };

    const initialOfferEventAttendance = filteredDates.reduce((acc, date) => {
        const id: string = date.id;

        const presentUserIds: string[] = [];
        const absentUserIds: string[] = [];

        enrollments.forEach((enrollment) => {
            const attendance = enrollment.attendance?.filter(
                (attendanceObj) => attendanceObj.offerEventDate?.id === id
            );

            if (!attendance?.length) return;

            attendance.forEach((attendanceObj) => {
                if (attendanceObj.status === IAttendanceStatusChoices.Present) {
                    presentUserIds.push(enrollment.user.id);
                } else if (
                    attendanceObj.status === IAttendanceStatusChoices.Absent
                ) {
                    absentUserIds.push(enrollment.user.id);
                }
            });
        });

        acc[id] = { presentUserIds, absentUserIds };

        return acc;
    }, {} as IEnrollmentAttendanceDialogValues);

    const initialValues: IEnrollmentAttendanceDialogValues = {
        ...initialOfferEventAttendance,
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async (
                values: IEnrollmentAttendanceDialogValues,
                actions
            ) => {
                await handleUpdateAttendance(values);

                actions.resetForm();
            }}
        >
            {({
                dirty,
                initialValues,
                values,
                setValues,
                resetForm,
                submitForm,
            }) => {
                let hasInitialPresentUsers = false;
                let hasInitialAbsentUsers = false;
                const toggleValues: Record<
                    string,
                    IAttendanceStatusChoices | undefined
                > = {};

                filteredDates.forEach((date) => {
                    const { id } = date;

                    const dateValue = values[id];

                    if (!dateValue) return;

                    const initialDateValue = initialValues[id];

                    const initialPresentUserIds =
                        initialDateValue.presentUserIds;
                    const initialAbsentUserIds = initialDateValue.absentUserIds;
                    const presentUserIds = dateValue.presentUserIds;
                    const absentUserIds = dateValue.absentUserIds;

                    if (
                        !hasInitialPresentUsers &&
                        initialPresentUserIds?.length
                    ) {
                        hasInitialPresentUsers = true;
                    }

                    if (
                        !hasInitialAbsentUsers &&
                        initialAbsentUserIds?.length
                    ) {
                        hasInitialAbsentUsers = true;
                    }

                    // The toggle value is based on if every selected user has either the present or absent
                    // attendance status
                    if (
                        !!presentUserIds?.length &&
                        enrollments.every(
                            (enrollment) =>
                                !!presentUserIds?.includes(enrollment.user.id)
                        )
                    ) {
                        toggleValues[id] = IAttendanceStatusChoices.Present;
                    } else if (
                        !!absentUserIds?.length &&
                        enrollments.every(
                            (enrollment) =>
                                !!absentUserIds?.includes(enrollment.user.id)
                        )
                    ) {
                        toggleValues[id] = IAttendanceStatusChoices.Absent;
                    } else {
                        // Reset to "No Status"
                        toggleValues[id] = undefined;
                    }
                });

                const allDatesNoStatus = Object.keys(toggleValues).every(
                    (id) => !toggleValues[id]
                );
                const allDatesStatus = Object.keys(toggleValues).every(
                    (id) => !!toggleValues[id]
                );

                const isInitiallyNoStatus =
                    allDatesNoStatus &&
                    !hasInitialPresentUsers &&
                    !hasInitialAbsentUsers;

                const submitDisabled =
                    isInitiallyNoStatus || (allDatesStatus && !dirty);

                return (
                    <ConfirmDialog
                        confirmText={translate(
                            'offerEventEnrollmentAttendanceDialog.btnText'
                        )}
                        disabled={submitDisabled}
                        loading={loading}
                        open={open}
                        title={translate(
                            'offerEventEnrollmentAttendanceDialog.title'
                        )}
                        onCancel={() => {
                            resetForm();

                            onClose();
                        }}
                        onClose={() => {
                            resetForm();

                            onClose();
                        }}
                        onConfirm={async () => {
                            // If toggle value is "No Status" and there are initial present or absent users,
                            // we want to give them all the "No Status" status
                            if (
                                allDatesNoStatus &&
                                (hasInitialPresentUsers ||
                                    hasInitialAbsentUsers)
                            ) {
                                const newValues = Object.keys(values).reduce(
                                    (acc, key) => {
                                        const dateValue = values[key];

                                        const presentUserIds =
                                            dateValue.presentUserIds || [];
                                        const absentUserIds =
                                            dateValue.absentUserIds || [];
                                        const noStatusUserIds = [
                                            ...(dateValue.noStatusUserIds ||
                                                []),
                                            ...presentUserIds,
                                            ...absentUserIds,
                                        ];

                                        return {
                                            ...acc,
                                            [key]: {
                                                presentUserIds: [],
                                                absentUserIds: [],
                                                noStatusUserIds:
                                                    noStatusUserIds,
                                            },
                                        };
                                    },
                                    {} as IEnrollmentAttendanceDialogValues
                                );

                                await setValues(newValues);
                            }

                            submitForm();
                        }}
                    >
                        {translate(
                            'offerEventEnrollmentAttendanceDialog.text',
                            {
                                count: enrollmentCount,
                            }
                        )}

                        {filteredDates.map((date) => {
                            const { id, startDate } = date;

                            const dateValue = values[id];

                            if (!dateValue) return;

                            const startDateString = startDate
                                ? formatDate(startDate, 'dd MMMM hh:mm')
                                : '';

                            return (
                                <Box
                                    key={id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        my: 2,
                                    }}
                                >
                                    <Box sx={{ mr: 2 }}>
                                        <ButtonToggle
                                            noDefault
                                            buttons={[
                                                {
                                                    key: IAttendanceStatusChoices.Present,
                                                    icon: mdiCheck,
                                                    color: 'success',
                                                },
                                                {
                                                    key: IAttendanceStatusChoices.Absent,
                                                    icon: mdiClose,
                                                    color: 'error',
                                                },
                                            ]}
                                            value={toggleValues[id]}
                                            onClickButton={(key) => {
                                                const dateAttendanceValues =
                                                    getEnrollmentAttendanceValues(
                                                        dateValue,
                                                        key,
                                                        enrollments
                                                    );

                                                const newValues = {
                                                    ...values,
                                                    [id]: dateAttendanceValues,
                                                };

                                                setValues(newValues);
                                            }}
                                        />
                                    </Box>

                                    {startDateString}
                                </Box>
                            );
                        })}

                        <Divider sx={{ pt: 2, pb: 1 }} />
                    </ConfirmDialog>
                );
            }}
        </Formik>
    );
};
